import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ApiClient from '../methods/api/apiClient';
import datepipeModel from '../models/datepipemodel';
import { encryptId } from '../components/common/Encryption/encryption';

const RsvpConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);

  const eventId = searchParams.get('eventId');
  const status = searchParams.get('status');
  const error = searchParams.get('error');
  const attendeeEmail = searchParams.get('email');

  const [loading, setLoading] = useState(true);
  const [eventData, setEventData] = useState<any>(null);

  useEffect(() => {
    if (error) {
      setLoading(false);
      return;
    }
    if (!eventId || !status) {
      setLoading(false);
      return;
    }
    // If logged in as a different user than the RSVP recipient, go straight to event page
    if (user?._id && attendeeEmail && user.email?.toLowerCase() !== attendeeEmail.toLowerCase()) {
      encryptId(String(eventId)).then(encrypted => navigate(`/event/detail/${encrypted}`, { replace: true }));
      return;
    }
    if (user?._id) {
      loadEventDetails(eventId);
    } else {
      setLoading(false);
    }
  }, [eventId, status, error]);

  const loadEventDetails = async (eid: string) => {
    try {
      const eventRes = await ApiClient.get('api/event/details', { id: eid });
      if (eventRes.success) setEventData(eventRes.data);
    } catch (err: any) {
      // Event details may fail for non-logged-in users
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">&#10007;</div>
          <h1 className="text-2xl font-bold mb-2">Invalid Link</h1>
          <p className="text-gray-600 mb-4">This RSVP link is not valid or has expired.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Confirming your RSVP...</div>;
  }

  if (!eventId || !status) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">&#10007;</div>
          <h1 className="text-2xl font-bold mb-2">Invalid Link</h1>
          <p className="text-gray-600 mb-4">This RSVP link is not valid.</p>
        </div>
      </div>
    );
  }

  const handleCheckEventDetails = async () => {
    const encryptedId = await encryptId(String(eventId));
    if (user?._id) {
      navigate(`/event/detail/${encryptedId}`);
    } else {
      navigate(`/login?redirect=${encodeURIComponent(`/event/detail/${encryptedId}`)}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md w-full">
        <div className="text-green-500 text-6xl mb-4">&#10003;</div>

        <h1 className="text-2xl font-bold mb-2">
          {status === 'Yes' ? 'RSVP Confirmed — You\'re attending!' : 'RSVP Confirmed — You\'re not attending.'}
        </h1>

        {eventData && (
          <div className="mb-6">
            <p className="text-gray-600 mb-2">
              {status === 'Yes'
                ? `You've confirmed your attendance for:`
                : `You've indicated you cannot attend:`}
            </p>
            <p className="font-semibold text-lg">{eventData.title}</p>
            <p className="text-gray-500 text-sm mt-2">
              {datepipeModel.formatDateToTimezone(eventData.date, eventData.timezone)}
            </p>
            <p className="text-gray-500 text-sm">{eventData.address}</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button
            onClick={handleCheckEventDetails}
            className="bg-orange-500 text-white px-6 py-3 rounded hover:bg-orange-600 w-full"
          >
            Check Event Details
          </button>
          {user?._id && (
            <button
              onClick={() => navigate('/')}
              className="border border-gray-300 text-gray-700 px-6 py-3 rounded hover:bg-gray-50 w-full"
            >
              Back to Home
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RsvpConfirmation;
